import { useEffect, useMemo } from 'react';
import { get } from 'lodash';
import { RadioGroup } from '@lendi-ui/radio';
import { Body } from '@lendi-ui/typography';

import { BUSINESS_STRUCTURE, EMPLOYMENT_TYPE, FIELD_PATHS, RELATED_COMPANY_TYPE } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { Company, CompanyEntityIndex, CompanyOwner, EmploymentEntityIndex, StoreEmployment } from '@funnels/types';
import { initialFormValues } from '@funnels/utils';

import { FunnelGrid, FunnelGridUnit } from '../../Common';
import HiddenField from '../../Fields/HiddenField';

const isCompanyValidForSelection = (
  company: Company,
  employments: StoreEmployment[],
  employmentIndex: number,
  applicantId: string,
  isCurrentRole: boolean
) => {
  // the applicant cannot have the same company for primary and secondary employments
  // (i.e. a same company cannot appear more than once for current employment)
  const companyClientId = get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0));
  // a valid company should comply following criteria:
  return [
    // 1. has a name
    !!get({ companies: [company] }, FIELD_PATHS.COMPANY_BUSINESS_NAME(0)),
    // 2. is a self-employed business
    get({ companies: [company] }, FIELD_PATHS.COMPANY_TYPE(0)) === RELATED_COMPANY_TYPE.SELF_EMPLOYMENT_BUSINESS,
    // 3. is not a SOLE_TRADER, or if it is, it should not be linked with any other applicant's employment
    get({ companies: [company] }, FIELD_PATHS.COMPANY_BUSINESS_STRUCTURE(0)) !== BUSINESS_STRUCTURE.SOLE_TRADER ||
      !employments.some(
        (_, index) =>
          get({ employments }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(index)) !== applicantId &&
          get({ employments }, FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(index)) === companyClientId
      ),
    // 4. is a previous role, or if not, there is no other PRIMARY OR SECONDARY employment
    //    for current applicant linked to this company
    !isCurrentRole ||
      !employments.some(
        (_, index) =>
          employmentIndex !== index &&
          get({ employments }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(index)) === applicantId &&
          get({ employments }, FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(index)) === companyClientId &&
          [EMPLOYMENT_TYPE.PRIMARY, EMPLOYMENT_TYPE.SECONDARY].includes(
            get({ employments }, FIELD_PATHS.EMPLOYMENT_TYPE(index))
          )
      ),
  ].every(Boolean);
};

interface CompanySelectorProps extends CompanyEntityIndex, EmploymentEntityIndex {
  isInCustomerView?: boolean;
}

export const CompanySelector = (props: CompanySelectorProps) => {
  const { employmentIndex, companyIndex, isInCustomerView = false } = props;
  const [formValues, setFormValue] = useFormValues();
  const employments: StoreEmployment[] = get(formValues, FIELD_PATHS.EMPLOYMENTS(), []);
  const companies: Company[] = get(formValues, FIELD_PATHS.COMPANIES(), []);
  const applicantId = get(formValues, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(employmentIndex));
  const currentCompanyClientId = get(formValues, FIELD_PATHS.COMPANY_CLIENT_ID(companyIndex));
  const employmentStatus = get(formValues, FIELD_PATHS.EMPLOYMENT_TYPE(employmentIndex));
  const isCurrentRole = [EMPLOYMENT_TYPE.PRIMARY, EMPLOYMENT_TYPE.SECONDARY].includes(employmentStatus);
  const newCompany = useMemo(() => initialFormValues.INITIAL_COMPANY(true), []);
  const newCompanyClientId = get({ companies: [newCompany] }, FIELD_PATHS.COMPANY_CLIENT_ID(0));

  const availableSelfEmployedCompanies = companies.filter(
    (company) =>
      get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0)) !== newCompanyClientId &&
      isCompanyValidForSelection(company, employments, employmentIndex, applicantId, isCurrentRole)
  );

  const selectCompany = (clientId: string) => {
    let selectedCompanyIndex = companies.findIndex(
      (_, index) => get({ companies }, FIELD_PATHS.COMPANY_CLIENT_ID(index)) === clientId
    );
    setFormValue(FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(employmentIndex), clientId);
    if (clientId === newCompanyClientId && selectedCompanyIndex === -1) {
      // add the new company if the new company hasn't been added into the list
      selectedCompanyIndex = companies.length;
      setFormValue(FIELD_PATHS.COMPANY(selectedCompanyIndex), newCompany);
    }
    const businessStructure = get({ companies }, FIELD_PATHS.COMPANY_BUSINESS_STRUCTURE(selectedCompanyIndex));
    if (businessStructure === BUSINESS_STRUCTURE.SOLE_TRADER && isCurrentRole) {
      setFormValue(FIELD_PATHS.COMPANY_OWNERS(selectedCompanyIndex), [
        {
          applicantId,
          percentOwned: 1,
        },
      ] as CompanyOwner[]);
    }
  };

  useEffect(() => {
    // automatically select the new company if there is no linked company and no other available companies
    if (!currentCompanyClientId && !availableSelfEmployedCompanies.length) {
      selectCompany(newCompanyClientId);
    }
  });

  return (
    <>
      {availableSelfEmployedCompanies.length > 0 && (
        <FunnelGrid mb="md">
          <FunnelGridUnit
            mb={isInCustomerView ? undefined : 'md'}
            size={{ xs: 1, sm: 1, xl: isInCustomerView ? 1 : 1 / 2 }}
          >
            <Body size="md">Select from one of the available businesses or enter new business details.</Body>
          </FunnelGridUnit>
          <FunnelGridUnit mb={isInCustomerView ? undefined : 'md'} size={{ xs: 1, sm: 1, xl: 1 / 2 }}>
            <RadioGroup
              size="sm"
              onChange={(e) => selectCompany(e.target.value)}
              selectedValue={currentCompanyClientId || ''}
            >
              <>
                {companies.map((company) => {
                  const companyClientId = get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0));
                  const businessName = get({ companies: [company] }, FIELD_PATHS.COMPANY_BUSINESS_NAME(0));
                  // company options rendering criteria:
                  // 1. is a current linked company
                  // 2. is a valid company for selection
                  if (
                    get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0)) !== newCompanyClientId &&
                    isCompanyValidForSelection(company, employments, employmentIndex, applicantId, isCurrentRole)
                  ) {
                    const label = `Use details from ${businessName}`;
                    return <RadioGroup.Radio key={companyClientId} label={label} value={companyClientId} />;
                  } else {
                    return null;
                  }
                })}
                <RadioGroup.Radio
                  key={newCompanyClientId}
                  label="Enter new business details"
                  value={newCompanyClientId}
                />
              </>
            </RadioGroup>
          </FunnelGridUnit>
        </FunnelGrid>
      )}
      <HiddenField
        name={FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(employmentIndex)}
        validate={(value: any) => {
          if (!value) {
            return 'Please select from one of the available businesses or enter new business details';
          }
        }}
      />
    </>
  );
};
